import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { api, errMsg } from "../lib/api";
import { useData } from "../lib/DataContext";
import { Stepper, TierBadge, Qty } from "./shared";

const TYPES = [
  { v: "receive", label: "Receive +" },
  { v: "shipment", label: "Ship / Use −" },
  { v: "adjustment", label: "Count Adjust ±" },
];

export default function AdjustDialog({ item, onClose, defaultType }) {
  const { refreshAll } = useData();
  const [type, setType] = useState("receive");
  const [allowRecv, setAllowRecv] = useState(false);
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setType(defaultType || (item?.tier === 3 ? "shipment" : item?.tier === 2 ? "adjustment" : "receive")); setQty(1); setNote("");
    if (item?.tier > 1) { api.get("/policy").then((r) => setAllowRecv(!!r.data.allow_receive_t23)); } }, [item?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const types = TYPES.filter((t) => t.v !== "receive" || item?.tier === 1 || allowRecv);
  if (!item) return null;

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/items/${item.id}/adjust`, { delta: qty, type, note });
      toast.success(`${item.name} updated`);
      await refreshAll();
      onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0F1623] border-[#334155] max-w-lg" data-testid="adjust-dialog">
        <DialogHeader>
          <TierBadge tier={item.tier} />
          <DialogTitle className="font-display text-2xl mt-2">{item.name}</DialogTitle>
          <div className="text-[#9CA3AF] text-sm">On hand: <Qty v={item.stock} unit={item.unit} className="text-white" /></div>
        </DialogHeader>
        <div className="flex justify-center gap-2" data-testid="adjust-type-group">
          {types.map((t) => (
            <button key={t.v} data-testid={`adjust-type-${t.v}`} onClick={() => setType(t.v)}
              className={`h-12 flex-1 max-w-[calc((100%-1rem)/3)] rounded-md text-sm font-semibold border ${type === t.v ? "bg-[#F59E0B] text-[#090D16] border-[#F59E0B]" : "border-[#334155] text-[#9CA3AF]"}`}>{t.label}</button>
          ))}
        </div>
        {type === "shipment" && item.tier === 1 && (
          <div data-testid="adjust-shipment-usage-warning" className="flex gap-2 p-3 rounded-md border border-[#F59E0B]/40 bg-[#F59E0B]/10 text-xs text-[#FCD34D]">
            <AlertTriangle className="w-4 h-4 shrink-0 text-[#F59E0B]" />
            <span>Ship / Use counts toward this material's daily usage (ADCR) and will raise reorder suggestions. To correct a count, use Count Adjust instead.</span>
          </div>
        )}
        <div className="flex justify-center py-2">
          <Stepper value={qty} onChange={setQty} min={type === "adjustment" ? -1e9 : 0} testId="adjust-qty" big />
        </div>
        <input data-testid="adjust-note-input" placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)}
          className="h-12 px-4 bg-[#090D16] border border-[#334155] rounded-md focus:outline-none focus:border-[#F59E0B]" />
        <button data-testid="adjust-submit-btn" disabled={busy || qty === 0} onClick={submit}
          className="h-14 rounded-md bg-[#F59E0B] text-[#090D16] font-display font-extrabold tracking-wide text-lg disabled:opacity-40 active:scale-[0.98] transition-transform">
          {busy ? "SAVING…" : "CONFIRM"}
        </button>
      </DialogContent>
    </Dialog>
  );
}
